import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';

import IdsModal from '../ids-modal/ids-modal';

import '../ids-icon/ids-icon';

import styles from './ids-message.scss';
import type IdsIcon from '../ids-icon/ids-icon';
import type IdsOverlay from '../ids-modal/ids-overlay';

type IdsMessageAlertType = 'none' | 'error' | 'alert' | 'success' | 'info' | 'warning' | 'question';

type IdsMessageWidthType = 'width' | 'maxWidth' | 'minWidth';

type IdsOverflowWrapType = 'normal' | 'break-word' | 'anywhere';

type IdsMessageButton = {
  /* Set the id */
  id?: string;
  /* Set the button text */
  text: string;
  /* Set the button appearance */
  appearance?: string;
  /* Handle the click event */
  click: (e: Event, modal: IdsMessage) => void;
  /* Make the button disabled if the value is true */
  disabled?: boolean;
  /* Set the button to the default button */
  isDefault?: boolean;
  /* Set the button to a cancel button */
  isCancel?: boolean;
};

export interface IdsMessageSettings {
  /* Set the id */
  id?: string;
  /* Set message status */
  status?: IdsMessageAlertType;
  /* Set the message title */
  title: string;
  /* Set the message contents */
  message?: string | null | undefined;
  /* Set the modal contents */
  contents?: string | null | undefined;
  /* Set the modal button alignment [left, right] */
  buttonAlignment?: string;
  /* Set the href link */
  buttons?: IdsMessageButton[];
  /* Set the width of the message */
  width?: string;
  /* Set the max-width of the message */
  maxWidth?: string;
  /* Set the min-width of the message */
  minWidth?: string;
}

/**
 * IDS Message Component
 * @type {IdsMessage}
 * @inherits IdsModal
 * @part popup - the popup outer element
 * @part overlay - the inner overlay element
 */
@customElement('ids-message')
@scss(styles)
export default class IdsMessage extends IdsModal {
  constructor() {
    super();

    if (!this.state) {
      this.state = { status: 'none' as IdsMessageAlertType };
    }
    this.state.message = '';
    this.state.status = 'none';
  }

  static get attributes() {
    return [
      ...super.attributes,
      attributes.MESSAGE,
      attributes.OPACITY,
      attributes.OVERFLOW_WRAP,
      attributes.STATUS,
      attributes.WIDTH,
      attributes.MAX_WIDTH,
      attributes.MIN_WIDTH
    ];
  }

  /**
   * @returns {void}
   */
  connectedCallback(): void {
    super.connectedCallback();

    // Update status and correct
    this.status = this.getAttribute(attributes.STATUS) as IdsMessageAlertType;
    // Update opacity and correct
    this.opacity = this.getAttribute(attributes.OPACITY);

    this.#applyOverflowWrap();

    // Remove fullsize setting
    this.fullsize = null;

    // Sanitizes the HTML in the component
    const currentContentEl = this.querySelector('*:not([slot])');
    if (currentContentEl) {
      this.message = currentContentEl.innerHTML;
    }
    this.#refreshStatus(this.status as IdsMessageAlertType);
  }

  /**
   * Navigates between buttons via ArrowLeft/ArrowRight keys
   * @param {KeyboardEvent} e the keyboard event
   */
  #handleArrowKeyNavigation(e: KeyboardEvent): void {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    const buttons = [...this.querySelectorAll<HTMLElement>('ids-modal-button, ids-button')]
      .filter((btn) => !btn.hasAttribute('disabled') && !btn.hasAttribute('hidden'));

    if (buttons.length < 2) return;

    const activeEl = document.activeElement;
    const currentBtn = activeEl?.closest('ids-modal-button, ids-button') as HTMLElement | null;
    const currentIndex = currentBtn ? buttons.indexOf(currentBtn) : -1;

    if (currentIndex === -1) return;

    const nextIndex = e.key === 'ArrowRight'
      ? (currentIndex + 1) % buttons.length
      : (currentIndex - 1 + buttons.length) % buttons.length;

    e.preventDefault();
    buttons[nextIndex].focus();
  }

  /**
   * Used for ARIA Labels and other content
   * @readonly
   * @returns {string} concatenating the status and title together.
   */
  get ariaLabelContent(): string {
    const status = this.status !== 'none' ? `${this.status}: ` : '';
    return `${status}${this.messageTitle}`;
  }

  /**
   * @returns {string} the current contents of the messsage
   */
  get message(): string {
    return this.state.message;
  }

  /**
   * @param {string} val the desired contents of the message element
   */
  set message(val: string) {
    const sanitizedVal = this.xssSanitize(val);
    if (sanitizedVal !== this.state.message) {
      this.#refreshMessage(sanitizedVal);
    }
  }

  /**
   * @returns {string} the current opacity of the overlay
   */
  get opacity(): string {
    return this.state.opacity;
  }

  /**
   * @param {string} val the desired opacity of the overlay
   */
  set opacity(val: string | null) {
    if (val) {
      this.state.opacity = val;

      const overlayElem = this.shadowRoot?.querySelector<IdsOverlay>('ids-overlay');
      if (overlayElem) {
        overlayElem.opacity = val;
      }
    }
  }

  /**
   * @returns {IdsOverflowWrapType | null} the current overflow-wrap value
   */
  get overflowWrap(): IdsOverflowWrapType | null {
    return this.getAttribute(attributes.OVERFLOW_WRAP) as IdsOverflowWrapType | null;
  }

  /**
   * @param {IdsOverflowWrapType | null} val the desired overflow-wrap value
   */
  set overflowWrap(val: IdsOverflowWrapType | null) {
    const validValues: IdsOverflowWrapType[] = ['normal', 'break-word', 'anywhere'];

    if (val && validValues.includes(val)) {
      this.setAttribute(attributes.OVERFLOW_WRAP, val);
    } else {
      this.removeAttribute(attributes.OVERFLOW_WRAP);
    }

    this.#applyOverflowWrap();
  }

  /**
   * Applies overflow-wrap CSS variable to the host element
   */
  #applyOverflowWrap(): void {
    const value = this.overflowWrap;
    if (value) {
      this.style.setProperty('--ids-message-overflow-wrap', value);
    } else {
      this.style.removeProperty('--ids-message-overflow-wrap');
    }
  }

  /**
   * Update the message style for a given property.
   * @param {Record<string, string>} inlineStyles The style properties to update.
   */
  #updateMessageStyle(inlineStyles: Record<string, string>): void {
    const idsMessageContent = this.container?.querySelector<HTMLElement>('.ids-message-content');
    if (idsMessageContent) {
      Object.entries(inlineStyles).forEach(([property, value]) => {
        // Convert camelCase to kebab-case
        const cssProperty = property.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        idsMessageContent.style.setProperty(cssProperty, value || '');
      });
    }
  }

  /**
   * Refreshes the state of the Message's Content
   * @param {string} content the new message content element
   */
  #refreshMessage(content: string | number): void {
    let messageEl = this.querySelector('*:not([slot])');
    if (!messageEl) {
      messageEl = document.createElement('div');
      this.appendChild(messageEl);
    } else {
      messageEl.innerHTML = '';
    }

    // Replace the message content
    messageEl.insertAdjacentHTML('afterbegin', String(content));
    this.state.message = content;
  }

  /**
   * @returns {string} the message's current status type
   */
  get status(): IdsMessageAlertType {
    return this.state.status || 'none';
  }

  /**
   * @param {string} val the message's new status type
   */
  set status(val: IdsMessageAlertType | null) {
    const currentValue = this.state.status;
    if (val !== currentValue) {
      this.state.status = val;

      if (typeof val === 'string' && val.length) {
        this.setAttribute(attributes.STATUS, val);
      } else {
        this.removeAttribute(attributes.STATUS);
      }

      this.#refreshStatus(val as IdsMessageAlertType);
      this.refreshAriaLabel();
    }
  }

  /**
   * Helper method to handle width attribute updates (width, maxWidth, minWidth).
   * @param {IdsMessageWidthType} type - The type of width (width, maxWidth, minWidth).
   * @param {string} value - The value to be set.
   * @private
   */
  #setWidthAttribute(type: IdsMessageWidthType, value: string | number) {
    // Return early if the value is a number
    if (typeof value === 'number') {
      throw new Error(`Invalid value type for ${type}: number is not allowed.`);
    }

    // Ensure the value is a string before trimming
    const trimmedValue = value.trim();

    // Only update if there's an actual change
    if (this.state[type] !== trimmedValue) {
      this.state[type] = trimmedValue;
      this.#updateMessageStyle({ [type]: trimmedValue });

      // Define the corresponding attribute name
      const attributeName = {
        width: attributes.WIDTH,
        maxWidth: attributes.MAX_WIDTH,
        minWidth: attributes.MIN_WIDTH
      }[type];

      if (attributeName) {
        // Add or remove attribute based on the presence of width
        if (trimmedValue) {
          this.setAttribute(attributeName, trimmedValue);
        } else {
          this.removeAttribute(attributeName);
        }
      }
    }
  }

  /**
   * Set the width of the message.
   * @param {string | null | undefined} value - The width of the message.
   */
  set width(value: string | null | undefined) {
    this.#setWidthAttribute('width', value ?? '');
  }

  get width(): string | null | undefined {
    return this.state.width || '';
  }

  /**
   * Set the max-width of the message.
   * @param {string | undefined} value - The max-width of the message.
   */
  set maxWidth(value: string | undefined) {
    this.#setWidthAttribute('maxWidth', value ?? '');
  }

  get maxWidth(): string {
    return this.state.maxWidth || '';
  }

  /**
   * Set the min-width of the message.
   * @param {string | undefined} value - The min-width of the message.
   */
  set minWidth(value: string | undefined) {
    this.#setWidthAttribute('minWidth', value ?? '');
  }

  get minWidth(): string {
    return this.state.minWidth || '';
  }

  /**
   * @param {string} val the value of the status icon
   * @returns {void}
   */
  #refreshStatus(val: IdsMessageAlertType): void {
    const header = this.container?.querySelector('.ids-modal-header');
    let icon = header?.querySelector<IdsIcon>('ids-icon');
    val = val === 'warning' ? 'alert' : val;
    const iconName = val === 'question' ? 'question-mark' : val;
    if (val && val !== 'none') {
      if (!icon) {
        header?.insertAdjacentHTML('afterbegin', `<ids-icon slot="icon" icon="${iconName}" class="ids-icon ids-message-status"></ids-icon>`);
        icon = header?.querySelector('ids-icon');
      }

      if (icon) {
        icon.icon = iconName;
        this.#setIconColor(icon, val);
      }
      header?.classList.remove('no-status-icon');
    } else {
      icon?.remove();
      header?.classList.add('no-status-icon');
    }
  }

  /**
   * Changes the color of the Status Icon
   * @param {Element} iconEl the icon element to update
   * @param {string} thisStatus the status string to apply as a CSS class
   * @returns {void}
   */
  #setIconColor(iconEl: Element, thisStatus: string): void {
    const iconElClassList = iconEl.classList;
    ['none', 'default', 'error', 'alert', 'success', 'info', 'warning', 'question'].forEach((status) => {
      thisStatus = thisStatus === 'alert' ? 'warning' : thisStatus;
      status = status === 'alert' ? 'warning' : status;
      if (thisStatus !== 'none' && thisStatus === status) {
        iconElClassList.add(status);
      } else {
        iconElClassList.remove(status);
      }
    });
  }

  isServiceMessage = false;

  addOpenEvents(): void {
    super.addOpenEvents();
    this.offEvent('keydown.arrow-nav');
    this.onEvent('keydown.arrow-nav', this, (e: KeyboardEvent) => this.#handleArrowKeyNavigation(e));
  }

  removeOpenEvents(): void {
    super.removeOpenEvents();
    this.offEvent('keydown.arrow-nav');
  }

  /**
   * Show the message
   * @param {IdsMessageSettings} settings  The settings for the message
   */
  async show(settings?: IdsMessageSettings) {
    const {
      id,
      status,
      message,
      title,
      buttons,
      buttonAlignment,
    } = settings || {};

    if (settings) this.isServiceMessage = true;
    if (id) this.setAttribute('id', id);
    if (title) this.insertAdjacentHTML('afterbegin', `<ids-text slot="title" font-size="24" type="h2">${title}</ids-text>`);
    if (status) this.status = status;
    if (message) this.insertAdjacentHTML('beforeend', `<ids-text align="left">${message}</ids-text>`);

    if (buttons) {
      let btnGroupTemplate = `<ids-button-group slot="buttons" ${buttonAlignment && buttonAlignment === 'left' ? `direction="row"` : `direction="row-reverse"`}>
      <ids-button-group-section gap="8">`;

      // Sort the buttons to make the primary go last of the loops
      buttons.sort((a: any, b: any) => (a.isDefault ? 1 : 0) - (b.isDefault ? 1 : 0));

      let i = 1;
      for (const button of buttons) {
        const btnId = button.id || `btn-${i}`;
        const disabled = button.disabled === true;
        const buttonHTML = `<ids-modal-button id="${btnId}" disabled="${disabled}" appearance="${button.appearance || (button.isDefault ? 'primary' : 'secondary')}"${button.isCancel ? ' cancel' : ''}>
          <span>${button.text}</span>
        </ids-modal-button>`;
        btnGroupTemplate += buttonHTML;
        i++;
      }

      btnGroupTemplate += `</ids-button-group-section></ids-button-group>`;
      this.insertAdjacentHTML('beforeend', btnGroupTemplate);

      buttons.forEach((button: any, index: number) => {
        const btnId = button.id || `btn-${index + 1}`;
        document.querySelector(`#${btnId}`)?.addEventListener('click', (e) => button.click(e, this as any));
      });
    }
    await super.show();

    // Override z-index for message to ensure it appears above modals
    // Note: z-index is controlled via --ids-z-index-message CSS variable
    this.style.setProperty(attributes.Z_INDEX, 'var(--ids-z-index-message)');
  }

  /**
   * Hides the message
   * @returns {Promise<void>}
   */
  async hide() {
    await super.hide();
    if (this.isServiceMessage) {
      this.remove();
    }
  }
}
